.button {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: var(--spacing-4);
  border: 1px solid transparent;
  font-family: var(--font-family);
  font-weight: var(--text-weight-medium);
  cursor: pointer;
  user-select: none;
  white-space: nowrap;
  box-sizing: border-box;
  /* Focus ring is a 1px outset box-shadow (per Figma "Focus" state) so it
   * layers cleanly over the variant's background without pushing layout.
   * We animate box-shadow + background-color + border-color + filter. */
  transition:
    background-color var(--motion-duration-fast) ease,
    border-color var(--motion-duration-fast) ease,
    box-shadow var(--motion-duration-fast) ease,
    color var(--motion-duration-fast) ease,
    filter var(--motion-duration-fast) ease;
}

.button:focus-visible {
  outline: none;
  /* 1px outset ring matching Figma's "Focus" state. Uses --text-primary
   * because it swaps light/dark automatically — neutral-600 in light,
   * near-white in dark — which is exactly what the Figma ring calls for. */
  box-shadow: 0 0 0 1px var(--text-primary);
}

.button:disabled {
  cursor: not-allowed;
  opacity: 0.5;
}

.label {
  /* Label is a span so leading/trailing icons align on the button's
   * central axis regardless of text wrapping. min-width:0 lets flex
   * shrinking work if the parent is narrow. */
  min-width: 0;
}

.fullWidth {
  width: 100%;
}

/* ─── Sizes ──────────────────────────────────────────────────────── */

/* xs — the tiny "Default" variant in Figma. 12px text, 14px icon,
 * rounded-12, narrower vertical padding. */
.size-xs {
  padding: var(--spacing-2) var(--spacing-8);
  border-radius: var(--radius-12);
  font-size: var(--text-size-xs);
  line-height: var(--text-lh-xs);
  letter-spacing: var(--text-xs-tracking);
  min-height: 24px;
}

/* s — rounded-8, 14px text, 16px icon. */
.size-s {
  padding: var(--spacing-4) var(--spacing-8);
  border-radius: var(--radius-8);
  font-size: var(--text-size-s);
  line-height: var(--text-lh-s);
  min-height: 28px;
}

/* m — the default size. Fixed 40px tall per Figma (h-[40px] p-[8px]). */
.size-m {
  padding: var(--spacing-8);
  border-radius: var(--radius-8);
  font-size: var(--text-size-s);
  line-height: var(--text-lh-s);
  height: 40px;
}

/* ─── Variants ───────────────────────────────────────────────────── */

/* Primary — vertical cyan→green gradient. The two stops (Cyan/200,
 * Green/300) are raw primitives in the Figma design system and don't
 * have dedicated semantic aliases yet. Hardcoded here with a TODO for
 * design to expose --button-primary-from / --button-primary-to. The
 * same gradient is used in both light and dark modes (the Figma file
 * shows identical stops).
 *
 * Label color is also hardcoded to #171717 in BOTH modes, not
 * `var(--text-primary)`. In dark mode --text-primary resolves to #f5f5f5,
 * which would give near-white text on the light cyan/green gradient —
 * roughly a 2.2:1 contrast ratio, well below WCAG AA (4.5:1). Since the
 * gradient is constant across modes, the label should be too. When
 * design exposes --button-primary-from / --button-primary-to, also
 * request a --button-primary-label token and swap this literal. */
.variant-primary {
  /* TODO: replace with --button-primary-from / --button-primary-to
   * and --button-primary-label once design exposes them as semantic
   * tokens in Figma. */
  background: linear-gradient(180deg, #a5f3fc 0%, #6ee7b7 100%);
  color: #171717;
  border-color: transparent;
  /* The base .button has `border: 1px solid transparent` so variants with
   * real borders (outline) don't shift size against variants without. For
   * gradient backgrounds, though, the default `background-origin: padding-box`
   * means the gradient's 0%/100% stops anchor at the padding edges — so
   * the 1px transparent border on top/bottom shows as a visible strip of
   * either the parent background or the extended edge color. Forcing
   * `background-origin: border-box` makes the gradient fill the full
   * border-box cleanly with no strips. */
  /* background-origin: border-box is the sole fix — it anchors the gradient
   * stops at the border edge rather than the padding edge, eliminating the
   * visible strip at the top/bottom of the 1px transparent border. The default
   * value of background-clip is already border-box so no declaration needed. */
  background-origin: border-box;
}
.variant-primary:hover:not(:disabled) {
  filter: brightness(0.95);
}
.variant-primary:active:not(:disabled) {
  filter: brightness(0.9);
}

/* Secondary — neutral fill, primary text. No dedicated hover token
 * in the Figma file for Secondary (the "hover" variant isn't defined
 * in the component set) so we fall back to a subtle brightness bump. */
.variant-secondary {
  background: var(--button-secondary-default);
  color: var(--text-primary);
  border-color: transparent;
}
.variant-secondary:hover:not(:disabled) {
  background: var(--button-secondary-hover);
}

/* Outline — bg-primary fill, neutral border. Hover paints in a subtle
 * background via --button-outline-hover (Figma defines this explicitly). */
.variant-outline {
  background: var(--bg-primary);
  color: var(--text-primary);
  border-color: var(--button-outline-default);
}
.variant-outline:hover:not(:disabled) {
  background: var(--button-outline-hover);
}

/* Ghost — no background, no border, primary text. Hover paints the same
 * subtle fill as Outline. */
.variant-ghost {
  background: transparent;
  color: var(--text-primary);
  border-color: transparent;
}
.variant-ghost:hover:not(:disabled) {
  background: var(--button-outline-hover);
}

/* Negative — destructive fill, on-dark text. */
.variant-negative {
  background: var(--button-negative);
  color: var(--text-on-dark);
  border-color: transparent;
}
.variant-negative:hover:not(:disabled) {
  filter: brightness(0.95);
}
.variant-negative:active:not(:disabled) {
  filter: brightness(0.9);
}

/* Loading — keep the label visible (so the button width doesn't jump)
 * but swap the leading icon slot for the spinner. The button itself is
 * disabled via the `disabled` attribute set on the <button>, so no
 * pointer interaction. */
.loading {
  cursor: wait;
}
